// Co-op (supplier trading) accounts in the cashbook — the pure pieces (plan/supplier-cashbooks.md §9). A co-op is a
// group of section cashbooks (/bank-accounts rows carrying group_id, section_code, section_label, group_name,
// group_provider); /coops lists the company's co-ops (also those with no cashbook yet) and the sub-accounts each co-op
// can have (providers); /statement-sets lists its imported statements, newest first, each with a status
// (reconciling | held | posted) and the cashbooks it reached. cashbook.js draws them; coop_review.js reviews one set.
import { parseAmount } from '../shared/formats.js';

/** The cards strip's order: plain bank cards first (API order), then one block per co-op (by name) holding its
 * section cashbooks (by section code). A co-op known only from /coops (no cashbook yet) still gets its block.
 * balanceOf(card) is the card's own balance (the screen's bankFigures); a co-op's balance is the sum of its sections'
 * that the farmer owes the co-op, and `funds` the sum of the farmer's own funds at it (the cards the API marks
 * own_funds, plan §12.5 — an asset, never netted against what is owed; null when it has none). */
export function groupCards(accounts, coops, balanceOf){
  const bal = typeof balanceOf === 'function' ? balanceOf : (a => Number(a.balance || 0));
  const banks = [], byGroup = new Map();
  const groupOf = id => {
    if(!byGroup.has(id)) byGroup.set(id, { id, name: '', provider: '', client_ref: '', cards: [], balance: 0, funds: null });
    return byGroup.get(id);
  };
  for(const c of coops || []){
    if(!c || c.id === null || c.id === undefined) continue;
    const g = groupOf(Number(c.id));
    g.name = c.name || g.name; g.provider = c.provider || g.provider; g.client_ref = c.client_ref || g.client_ref;
  }
  for(const a of accounts || []){
    if(a.group_id === null || a.group_id === undefined || a.group_id === ''){ banks.push(a); continue; }
    const g = groupOf(Number(a.group_id));
    g.name = g.name || a.group_name || '';
    g.provider = g.provider || a.group_provider || '';
    g.cards.push(a);
  }
  const groups = [...byGroup.values()];
  for(const g of groups){
    g.cards.sort((x, y) => String(x.section_code || '').localeCompare(String(y.section_code || ''))
      || String(x.name || '').localeCompare(String(y.name || '')));
    const sum = cards => Math.round(cards.reduce((s, a) => s + Number(bal(a) || 0), 0) * 100) / 100;
    const own = g.cards.filter(a => a.own_funds);
    g.balance = sum(g.cards.filter(a => !a.own_funds));
    g.funds = own.length ? sum(own) : null;
  }
  groups.sort((x, y) => String(x.name).localeCompare(String(y.name)) || x.id - y.id);
  return { banks, coops: groups };
}

/** A section card's own name: its section's label (the card name repeats the co-op's: "VKB — Monthly account"). */
export function sectionName(card){
  if(card && card.section_label) return card.section_label;
  const n = String((card && card.name) || '');
  const at = n.indexOf(' — ');
  return at > 0 ? n.slice(at + 3) : n;
}

/** The co-op's newest imported statement (the API lists them newest first), or null. */
export function latestSet(sets, groupId){
  return (sets || []).find(s => Number(s.group_id) === Number(groupId)) || null;
}

/** The statements of this cashbook's that do not tie to their PDF (held): nothing of them posts. */
export function heldSetsFor(sets, accountId){
  return (sets || []).filter(s => s.status === 'held'
    && (s.statements || []).some(st => Number(st.bank_account_id) === Number(accountId)));
}

/** The sets that reached this cashbook and are not posted in full yet — the ones whose status can still change. */
export function openSetsFor(sets, accountId){
  return (sets || []).filter(s => s.status !== 'posted'
    && (s.statements || []).some(st => Number(st.bank_account_id) === Number(accountId)));
}

/** A set's status as the review and the cards show it: { tone: 'ok' | 'held' | 'wait', key, text } (i18n key and
 * its English). 'reconciling' = it ties to its statement (cleared to post); 'posted' = every line of it posted. */
export function setStatus(status){
  if(status === 'held') return { tone: 'held', key: 'coop.status_held', text: 'Held' };
  if(status === 'posted') return { tone: 'ok', key: 'coop.status_posted', text: 'Posted' };
  if(status === 'reconciling') return { tone: 'ok', key: 'coop.status_ties', text: 'Ties' };
  return { tone: 'wait', key: 'coop.status_checking', text: 'Checking' };
}

/** What holds a co-op statement, from its tie-out (GET /statement-sets/{id}): { pdf, carry, lock, stale } — pdf: it
 * does not tie to its PDF (a cashbook, document or VAT figure off, or a problem of the tie-out's own); carry: its
 * sub-accounts do not carry on from the co-op's statement before (one in between missing, or differences no move
 * explains — the API's continuity); lock: the move between the co-op's sub-accounts it opens with falls in a locked
 * financial year; stale: the difference booked for it (its review's "Book the difference") is no longer what the
 * statements say. A held statement the API says nothing more of is held by its PDF; one that is not held, by nothing. */
export function heldWhy(diff){
  const d = diff || {}, c = d.continuity || null;
  if(d.status !== 'held') return { pdf: false, carry: false, lock: false, stale: false };
  const off = rows => (rows || []).some(r => r && r.ties === false);
  const own = (d.problems || []).length - ((c && c.problems) || []).length;
  const carry = !!c && (c.verdict === 'gap' || c.verdict === 'unexplained');
  const lock = !!c && c.verdict === 'moved' && !!c.locked && !c.booked;
  const stale = !!c && !!c.undo && c.verdict !== 'explained' && !carry;
  const pdf = off(d.cashbooks) || off(d.documents) || off(d.vat) || !!(d.diesel && d.diesel.ties === false)
    || !!(d.account_tie && d.account_tie.ties === false) || own > 0 || (!carry && !lock && !stale);
  return { pdf, carry, lock, stale };
}

/** What holds a statement (heldWhy) in words, one clause each, through the caller's t(key, fallback). */
export function heldReasons(why, t){
  return [why.pdf && t('coop.held_pdf', 'it does not tie to its PDF yet'),
          why.carry && t('coop.held_carry', 'it does not carry on from the supplier’s statement before'),
          why.lock && t('coop.held_lock', 'the move between the supplier’s sub-accounts it opens with falls in a locked financial year'),
          why.stale && t('coop.held_stale', 'the difference booked for it is no longer what the statements say')]
    .filter(Boolean);
}

/** The register's rows with a co-op cashbook's balance moves among them (GET …/register `moves`: the co-op moved a
 * balance between its sub-accounts with no line on any statement), each { ...move, kind: 'move' } by its date — on a
 * statement's first day, before that day's lines. dir: the register's date order, 'desc' (newest first) or 'asc'. A
 * move past the last row loaded waits until every row is loaded (complete), so it never sits above rows still to come.
 * The rows themselves are never changed. */
export function withMoves(rows, moves, { dir = 'desc', complete = false } = {}){
  const day = r => String(r.txn_date || r.date || '').slice(0, 10);
  const ms = (moves || []).slice().sort((a, b) => day(a).localeCompare(day(b)) || a.journal_id - b.journal_id);
  if(!ms.length) return (rows || []).slice();
  const asc = dir === 'asc', queue = asc ? ms : ms.reverse(), out = [];
  let i = 0;
  for(const r of rows || []){
    // oldest first: a move goes before the first row on or after its day; newest first: before the first row older
    while(i < queue.length && (asc ? day(queue[i]) <= day(r) : day(r) < day(queue[i]))) out.push({ ...queue[i++], kind: 'move' });
    out.push(r);
  }
  if(complete) while(i < queue.length) out.push({ ...queue[i++], kind: 'move' });
  return out;
}

/** The co-op statement sets a finished import job made (its files that are co-op statements), in file order. */
export function jobSets(result){
  return ((result && result.files) || [])
    .filter(f => f && f.is_coop && f.statement_set_id)
    .map(f => Number(f.statement_set_id));
}

/** A document's page as the page route takes it (0-based) from the page the read gives (1-based), or null. */
export function pageIndex(page){
  const n = Number(page);
  return Number.isInteger(n) && n >= 1 ? n - 1 : null;
}

/** The review's documents: the ones that do not tie first (largest difference first), then by date and reference. */
export function reviewDocuments(documents){
  const off = d => Math.max(Math.abs(Number(d.difference) || 0), Math.abs(Number(d.vat_difference) || 0));
  return (documents || []).slice().sort((x, y) => (x.ties === y.ties ? 0 : (x.ties ? 1 : -1))
    || off(y) - off(x)
    || String(x.date || '').localeCompare(String(y.date || ''))
    || String(x.ref || '').localeCompare(String(y.ref || '')));
}

/** The sub-accounts a co-op's cashbook can be made for, by hand: its provider's that a statement import brings in
 * (`imported` — since 2026-10-27 the farmer's own funds too, plan §12.5: shareholder loans, shares, a deposit or wallet,
 * each a cashbook of its own on the asset side), less those the co-op has a cashbook for. */
export function sectionChoices(providers, provider, taken){
  const p = (providers || []).find(x => x.provider === provider);
  const have = new Set((taken || []).map(c => String(c)));
  return p ? p.sections.filter(s => s.imported && !have.has(String(s.code))) : [];
}

/** The co-ops a held statement may be linked to: the same co-op (provider), or one made with none. */
export function linkableCoops(coops, provider){
  return (coops || []).filter(c => !c.provider || !provider || c.provider === provider);
}

/** Two account numbers at a co-op, both read, that are not the same number — the same written another way (case,
 * spaces, dashes) is the same (the API's supplier_statement_router._same_ref). */
export function refsDiffer(a, b){
  const x = String(a || '').trim().toUpperCase(), y = String(b || '').trim().toUpperCase();
  return Boolean(x && y) && x !== y && x.replace(/[^0-9A-Z]/g, '') !== y.replace(/[^0-9A-Z]/g, '');
}

/** Where the form on a held co-op statement starts (read: its needs_coop): {mode: 'existing' | 'new', groupId}. Of
 * the co-ops it may be linked to, only its own — no account number on one side, or the same — is chosen for it, and
 * only when it is the one co-op it may be linked to; one of another account number (a mismatch, the API asks first) is
 * offered, never chosen (2026-10-27: the member account asked about after a co-op was linked started on that co-op).
 * None its own: its co-op is a new one. */
export function coopLinkStart(coops, read){
  const r = read || {};
  const list = linkableCoops(coops, r.provider);
  const own = list.filter(c => !refsDiffer(c.client_ref, r.client_ref));
  return { mode: own.length ? 'existing' : 'new', groupId: list.length === 1 && own.length === 1 ? String(own[0].id) : '' };
}

/** A co-op line that moved money to an account at the co-op the books do not keep (the API's moved_to: OVK's "GRAAN
 * OORPL NA <number>", an account not the company's own there): it is on no statement imported here and has no cashbook,
 * so the line is the user's to book, to what that account is (Fable's final check, finding 7; 2026-10-26). '' for none. */
export function movedOutText(moved, t){
  if(!moved || !moved.account) return '';
  return t('cashbook.moved_out', 'Moved to {coop} account {account} — not on this statement and no cashbook here: book it to what that account is.')
    .replace('{coop}', moved.coop || '').replace('{account}', moved.account);
}

const blank = v => v === null || v === undefined || String(v).trim() === '';

/** A co-op cashbook's credit limit as its card says it (plan §9: "each sub-cashbook showing its own balance/limit"):
 * null without one; else {limit, left, over} — left: what the farmer may still owe on it (the API's credit_left: the
 * limit less what the cashbook owes now), never below 0; over: how far past the limit it owes (0 within it). */
export function limitFigures(card){
  if(!card || blank(card.credit_limit)) return null;
  const limit = Number(card.credit_limit);
  if(!Number.isFinite(limit)) return null;
  const given = blank(card.credit_left) ? limit : Number(card.credit_left);
  const left = Number.isFinite(given) ? given : limit;
  return { limit, left: left > 0 ? left : 0, over: left < 0 ? Math.round(-left * 100) / 100 : 0 };
}

/** A rate as a person writes it: 11.5, never 11.500 nor 11.499999999999998; none (null, blank) is '' — never 0. */
export function rateText(rate){
  if(blank(rate)) return '';
  const n = Number(rate);
  return Number.isFinite(n) ? String(Math.round(n * 1000) / 1000) : String(rate);
}

/** A co-op cashbook's terms and interest as its card says them (plan §1: "terms + interest per cashbook"): "30 days ·
 * 11.5% a year", '' for none. */
export function termsText(card, t){
  if(!card) return '';
  const bits = [];
  if(!blank(card.terms)) bits.push(String(card.terms).trim());
  if(!blank(card.interest_rate)) bits.push(t('coop.rate_year', '{rate}% a year').replace('{rate}', rateText(card.interest_rate)));
  return bits.join(' · ');
}

/** What the card's ✎ form sends (PUT /bank-accounts/{id}): {body} or {error}. The limit and the rate are read as the page
 * reads a typed amount (parseAmount, `marks` the company's currency marks: "500 000", "500,000", "R 500 000"; "11,5",
 * "11.5%"); an empty one is cleared (sent ''); the terms are trimmed to the 200 characters the API keeps. The farmer's
 * own funds at the co-op have no credit limit (the co-op lends nothing on them): none is sent for them. */
export function limitBody(card, { limit, terms, rate } = {}, marks, t){
  const body = {};
  if(!(card && card.own_funds)){
    if(blank(limit)) body.credit_limit = '';
    else {
      const n = parseAmount(String(limit), marks);
      if(n === null || n < 0) return { error: t('coop.limit_bad', 'The credit limit is an amount of 0 or more, e.g. 500000') };
      body.credit_limit = Math.round(n * 100) / 100;
    }
  }
  body.terms = blank(terms) ? '' : String(terms).trim().slice(0, 200);
  if(blank(rate)) body.interest_rate = '';
  else {
    const n = parseAmount(String(rate), ['%']);
    if(n === null || n < 0 || n > 100) return { error: t('coop.rate_bad', 'The interest rate is a percentage from 0 to 100, e.g. 11.5') };
    body.interest_rate = Math.round(n * 1000) / 1000;
  }
  return { body };
}
